import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Download, Calendar, IndianRupee, Users, Package, ShoppingCart } from 'lucide-react';
import './Reports.css';

const Reports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30days');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [granularity, setGranularity] = useState('daily');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(amount || 0));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Number(num || 0));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const headers = { 'x-admin-key': apiKey };

      const [sRes, tRes, vRes, pRes] = await Promise.all([
        fetch('/api/admin/reports/summary', { headers }),
        fetch(`/api/admin/reports/trend?granularity=${granularity}&days=${selectedPeriod === '7days' ? 7 : selectedPeriod === '30days' ? 30 : selectedPeriod === '90days' ? 90 : 365}`, { headers }),
        fetch('/api/admin/reports/top-vendors?limit=10', { headers }),
        fetch('/api/admin/reports/top-products?limit=10', { headers })
      ]);

      if (sRes.ok) setSummary(await sRes.json());
      if (tRes.ok) setTrend(await tRes.json());
      if (vRes.ok) setTopVendors(await vRes.json());
      if (pRes.ok) setTopProducts(await pRes.json());
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPeriod, granularity]);

  const summaryCards = [
    {
      key: 'ordersToday',
      title: 'Total Orders (Today)',
      value: summary?.today?.total_orders,
      icon: <ShoppingCart size={24} />, className: 'orders'
    },
    {
      key: 'ordersWeek',
      title: 'Total Orders (This Week)',
      value: summary?.week?.total_orders,
      icon: <ShoppingCart size={24} />, className: 'orders'
    },
    {
      key: 'ordersMonth',
      title: 'Total Orders (This Month)',
      value: summary?.month?.total_orders,
      icon: <ShoppingCart size={24} />, className: 'orders'
    },
    {
      key: 'revenueToday',
      title: 'Revenue (Today)',
      value: formatCurrency(summary?.today?.total_revenue),
      icon: <IndianRupee size={24} />, className: 'revenue'
    },
    {
      key: 'refundsToday',
      title: 'Refunds Issued (Today)',
      value: summary?.today?.refunds_issued,
      icon: <TrendingUp size={24} />, className: 'products'
    }
  ];

  return (
    <div className="reports">
      <div className="reports-header">
        <div className="header-content">
          <h1>Analytics & Reports</h1>
          <p>Comprehensive insights into your business performance</p>
        </div>
        <div className="header-actions">
          <div className="period-selector">
            <Calendar size={20} />
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="period-select"
            >
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
              <option value="1year">Last year</option>
            </select>
          </div>
          <div className="period-selector" style={{ marginLeft: 12 }}>
            <BarChart3 size={20} />
            <select value={granularity} onChange={(e) => setGranularity(e.target.value)} className="period-select">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <button className="btn btn-primary">
            <Download size={20} />
            Export Report
          </button>
        </div>
      </div>

      <div className="reports-stats">
        {summaryCards.map((card) => (
          <div key={card.key} className="stat-card">
            <div className={`stat-icon ${card.className}`}>
              {card.icon}
            </div>
            <div className="stat-content">
              <h3>{card.title}</h3>
              <p className="stat-value">{formatNumber(card.value)}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="reports-content">
        <div className="chart-section">
          <div className="section-header">
            <h2>Revenue & Orders Trend</h2>
            <div className="chart-legend">
              <div className="legend-item">
                <div className="legend-color revenue"></div>
                <span>Revenue</span>
              </div>
              <div className="legend-item">
                <div className="legend-color orders"></div>
                <span>Orders</span>
              </div>
            </div>
          </div>
          <div className="chart-container">
            <div className="chart-placeholder">
              <BarChart3 size={48} />
              <p>Revenue & Orders Chart</p>
              <span>Data points: {trend.length}</span>
            </div>
          </div>
        </div>

        <div className="tables-section">
          <div className="table-container">
            <div className="section-header">
              <h2>Top Performing Vendors</h2>
            </div>
            <div className="table-content">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Revenue</th>
                    <th>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {topVendors.map((v, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="vendor-info">
                          <div className="vendor-rank">#{idx + 1}</div>
                          <span className="vendor-name">{v.vendor_name || v.vendor_id}</span>
                        </div>
                      </td>
                      <td className="revenue-cell">{formatCurrency(v.revenue)}</td>
                      <td className="orders-cell">{formatNumber(v.order_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="table-container">
            <div className="section-header">
              <h2>Top Selling Products</h2>
            </div>
            <div className="table-content">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Sales</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((p, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="product-info">
                          <div className="product-rank">#{idx + 1}</div>
                          <span className="product-name">{p.product_name || p.product_id}</span>
                        </div>
                      </td>
                      <td className="category-cell">{p.category}</td>
                      <td className="sales-cell">{formatNumber(p.sales)}</td>
                      <td className="revenue-cell">{formatCurrency(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading" style={{ marginTop: 12 }}>Loading analytics...</div>
      )}
    </div>
  );
};

export default Reports;
